import {useState} from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Wizard, {StepProps} from 'components/forms/Wizard'
import type {Alert as AlertType, Trigger, TriggerTypes} from 'services/types'
import {ChooseTrigger, NotificationMethods, BasicSettings} from './configure'

const documentationItems = {
  settings: [
    {
      title: 'What is an alert?',
      contents: (
        <Stack>
          <Typography>
            Alerts are notifications that are sent to you when something specific happens. For
            example:
          </Typography>
          <ul>
            <li>
              an alert that will send you an email whenever a specific model scores more than 90% on
              a specific evaluation
            </li>
            <li>an alert that will send a request to a webhook whenever a new model is added</li>
          </ul>
        </Stack>
      ),
    },
    {
      title: 'What is the repeat rate?',
      contents: [
        `Some alerts will only ever fire once. But some alerts can check for triggers that can happen often, e.g. model rank
           changes for models that are very close to each other.`,
        'You can control how often an alert can fire with the repeat rate:',
        <ul>
          <li>
            Only once - you'll be alerted when the trigger first fires, but no more, no matter how
            many subsequent trigger changes happen
          </li>
          <li>
            Once per day - there's a 24 hour cooldown between alerts, during which time we won't
            send any notifications
          </li>
          <li>Every time - each and every trigger fire will cause you to be notified</li>
        </ul>,
      ],
    },
  ],
  notifications: [
    {
      title: 'What are notification methods?',
      contents: [
        'Notification methods specify how we should let you be alerted.',
        'You can add up to 20 addititional notification methods.',
      ],
    },
    {
      title: 'Is there a default notification method?',
      contents: ["By default we will send alerts to your user account's email address."],
    },
  ],
  baseTriggerDocs: [
    {
      title: 'What are triggers?',
      contents: [
        'Triggers define what has to happen for an alert to be sent.',
        'There are various kinds of triggers with various levels of configurability.',
        `Most triggers allow you to select models that should be checked - triggers with
                 specified models or evaluations will only take these into consideration when checking
                 whether an alert should be sent - all other items will be ignored.`,
      ],
    },
    {
      title: 'Specific items vs filters',
      contents: [
        `When specifying models or evaluations to be considered by the trigger, you can either select
                specific items, or define a filter.`,
        `In the case of specific items, only the items you explicitly select by clicking them will be
                 used by the trigger.`,
        `Filters are more generic - you can use the search bar and filters to find items you're interested
                 in. Every time the trigger checks for items, it will apply your selected filters and use the resulting
                items.`,
        `Select specific items when you only want to check those specific items, e.g. you only want to check
                your specific version of Llama 3.`,
        `Use filters when you want to check for general things, e.g. any OpenAI model.`,
      ],
    },
  ],
}

const triggerDocs = (triggerType?: TriggerTypes) => [
  ...documentationItems.baseTriggerDocs,
  ...(documentationItems[triggerType as keyof typeof documentationItems] || []),
]

type AlertWizardProps = {
  initial?: AlertType
  onSave: (e: AlertType) => Promise<string | null>
  onCancel?: () => void
  basic?: boolean
  triggers?: boolean
  notifications?: boolean
  title: string
}
const AlertWizard = ({
  initial,
  onSave,
  onCancel,
  basic,
  triggers,
  notifications,
  title,
}: AlertWizardProps) => {
  const [alert, setAlert] = useState(initial || ({} as unknown as AlertType))

  const onChange = (field: string, val: any) => setAlert((e) => ({...e, [field]: val}))
  const onSubmit = async () => onSave(alert)

  const props = {alert, onChange}
  const steps = [
    basic && {
      label: 'Basic information',
      Step: BasicSettings,
      documentation: documentationItems.settings,
      extraProps: props,
    },
    notifications && {
      label: 'How should you be alerted?',
      Step: NotificationMethods,
      documentation: documentationItems.notifications,
      extraProps: props,
    },
    triggers && {
      label: 'What should trigger your Alert?',
      Step: ChooseTrigger,
      documentation: triggerDocs((alert.triggers && alert.triggers[0])?.type),
      extraProps: {
        ...props,
        trigger: alert.triggers && alert.triggers[0],
        triggerChanged: (t: Trigger) => onChange('triggers', [t]),
      },
    },
  ].filter(Boolean) as StepProps<any>[]
  if (steps.length) {
    return <Wizard title={title} onFinish={onSubmit} onCancel={onCancel} steps={steps} />
  }
  return null
}

export default AlertWizard
